import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, ShieldOff } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { OverviewToolbar } from "../components/layout/OverviewToolbar";
import {
  loadViewMode,
  saveViewMode,
  useFitsMultipleColumns,
  ViewToggle,
  type ViewMode,
} from "../components/layout/ViewToggle";
import { blacklistsApi, type BlacklistListItem } from "../api/blacklistsApi";

const BL_CARD_MIN = 280;

function isExpired(blockedTill: string | null | undefined): boolean {
  if (!blockedTill) return false;
  const d = new Date(blockedTill);
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

type LoadState =
  | { status: "loading" }
  | { status: "ready"; items: BlacklistListItem[] }
  | { status: "error"; message: string };

/**
 * Customer-side blacklists overview. Mirrors the legacy
 * CustomerNumbers/Blacklists Razor view (description / number / ANI /
 * blocked-till). Add Blacklist creates a fresh row server-side and lands
 * on the edit page so the customer can fill in the meaningful fields.
 */
export function BlacklistsPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [view, setView] = useState<ViewMode>(() => loadViewMode("blacklists"));
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showExpired, setShowExpired] = useState(false);
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);
  const canShowToggle = useFitsMultipleColumns(listRef, BL_CARD_MIN);

  const refresh = useCallback(async () => {
    try {
      const res = await blacklistsApi.list({ includeExpired: showExpired });
      setState({ status: "ready", items: res.items ?? [] });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load blacklists";
      setState({ status: "error", message });
    }
  }, [showExpired]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    if (state.status !== "ready") return [] as BlacklistListItem[];
    const q = search.trim().toLowerCase();
    if (!q) return state.items;
    return state.items.filter(
      (b) =>
        b.description.toLowerCase().includes(q) ||
        b.number.toLowerCase().includes(q) ||
        b.ani.toLowerCase().includes(q),
    );
  }, [state, search]);

  const onCreate = async () => {
    setCreateError(null);
    setCreating(true);
    try {
      const created = await blacklistsApi.create();
      navigate(`/numbers/blacklists/${created.blacklistId}`);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create blacklist";
      setCreateError(message);
      setCreating(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Blacklisted"
        actions={
          <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
            <Link
              to="/numbers"
              aria-label="Back to numbers"
              className="btn btn-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
            >
              <ArrowLeft size={14} /> Back to numbers
            </Link>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onCreate}
              disabled={creating}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Plus size={16} />
              {creating ? "Creating…" : "New entry"}
            </button>
          </div>
        }
      />
      <div className="dashboard">
        {createError && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            {createError}
          </div>
        )}
        {state.status === "loading" && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading blacklists…
          </div>
        )}
        {state.status === "error" && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Something went wrong.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>
              {state.message}
            </div>
          </div>
        )}
        {state.status === "ready" && state.items.length === 0 && (
          <EmptyState
            includeExpired={showExpired}
            onToggleExpired={() => setShowExpired((v) => !v)}
          />
        )}
        {state.status === "ready" && state.items.length > 0 && (
          <>
            <OverviewToolbar
              search={search}
              onSearch={setSearch}
              placeholder="Search by description, number or ANI"
              count={{ matched: filtered.length, total: state.items.length }}
              trailing={
                <div style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: "var(--portal-muted)",
                      fontSize: "0.85rem",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={showExpired}
                      onChange={(e) => setShowExpired(e.target.checked)}
                    />
                    Show expired
                  </label>
                  {canShowToggle && (
                    <ViewToggle
                      value={view}
                      onChange={(next) => {
                        setView(next);
                        saveViewMode("blacklists", next);
                      }}
                    />
                  )}
                </div>
              }
            />
            <div ref={listRef}>
              {filtered.length === 0 ? (
                <div className="tile" style={{ color: "var(--portal-muted)" }}>
                  No entries match "{search}".
                </div>
              ) : view === "card" || !canShowToggle ? (
                <BlacklistCardGrid
                  items={filtered}
                  onOpen={(id) => navigate(`/numbers/blacklists/${id}`)}
                />
              ) : (
                <BlacklistRowList
                  items={filtered}
                  onOpen={(id) => navigate(`/numbers/blacklists/${id}`)}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function EmptyState({
  includeExpired,
  onToggleExpired,
}: {
  includeExpired: boolean;
  onToggleExpired: () => void;
}) {
  return (
    <section
      className="tile"
      style={{
        maxWidth: 720,
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        gap: 18,
        alignItems: "flex-start",
        padding: 24,
      }}
    >
      <div
        className="tile-row-icon"
        style={{ width: 56, height: 56, borderRadius: 14 }}
        aria-hidden="true"
      >
        <ShieldOff size={28} />
      </div>
      <div>
        <h3 style={{ marginTop: 0 }}>
          {includeExpired ? "No entries on record" : "No active entries"}
        </h3>
        <p style={{ marginTop: 0 }}>
          {includeExpired
            ? "There are no blacklist entries for this customer."
            : "Block individual callers from reaching your numbers, or browse expired entries to see past blocks."}
        </p>
        {!includeExpired && (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginTop: 8 }}
            onClick={onToggleExpired}
          >
            Show expired entries
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * Status pill used both on the card and the row layout. We compute
 * Active/Expired purely client-side from blockedTill so the SPA can
 * always render the right colour even when the API returns expired
 * rows mixed in.
 */
function StatusPill({ blockedTill }: { blockedTill: string }) {
  const expired = isExpired(blockedTill);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        border: `1px solid ${expired ? "var(--portal-border)" : "color-mix(in srgb, var(--portal-primary) 35%, transparent)"}`,
        background: expired
          ? "var(--portal-surface)"
          : "color-mix(in srgb, var(--portal-primary) 12%, transparent)",
        color: expired ? "var(--portal-muted)" : "var(--portal-primary)",
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.02em",
      }}
    >
      {expired ? "Expired" : "Active"}
    </span>
  );
}

function BlacklistCardGrid({
  items,
  onOpen,
}: {
  items: BlacklistListItem[];
  onOpen: (id: number) => void;
}) {
  return (
    <div
      className="tile-cards"
      style={{ ["--tile-min" as never]: `${BL_CARD_MIN}px` }}
    >
      {items.map((b) => {
        const expired = isExpired(b.blockedTill);
        return (
          <button
            key={b.blacklistId}
            type="button"
            className="tile tile-link"
            onClick={() => onOpen(b.blacklistId)}
            style={{
              textAlign: "left",
              gap: 10,
              padding: 20,
              opacity: expired ? 0.7 : 1,
            }}
            aria-label={`Open ${b.description}`}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={b.description}
              >
                {b.description || "(no description)"}
              </div>
              <StatusPill blockedTill={b.blockedTill} />
            </div>
            <div
              style={{
                color: "var(--portal-muted)",
                fontSize: "0.85rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={b.number}
            >
              Number: {b.number || "—"}
            </div>
            <div
              style={{
                color: "var(--portal-muted)",
                fontSize: "0.85rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={b.ani}
            >
              ANI: {b.ani || "—"}
            </div>
            <div
              style={{
                color: "var(--portal-muted)",
                fontSize: "0.85rem",
              }}
            >
              Blocked till: {formatDate(b.blockedTill)}
            </div>
            <div
              style={{
                marginTop: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                color: "var(--portal-primary)",
                fontWeight: 600,
                fontSize: "0.85rem",
                gap: 4,
              }}
            >
              <Pencil size={14} /> Edit
            </div>
          </button>
        );
      })}
    </div>
  );
}

function BlacklistRowList({
  items,
  onOpen,
}: {
  items: BlacklistListItem[];
  onOpen: (id: number) => void;
}) {
  return (
    <div className="tile-rows">
      {items.map((b) => {
        const expired = isExpired(b.blockedTill);
        return (
          <button
            key={b.blacklistId}
            type="button"
            className="tile tile-link"
            onClick={() => onOpen(b.blacklistId)}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 16,
              padding: "12px 18px",
              textAlign: "left",
              opacity: expired ? 0.7 : 1,
            }}
            aria-label={`Open ${b.description}`}
          >
            <div
              style={{
                flex: "2 1 0",
                minWidth: 0,
                fontWeight: 600,
                fontSize: "1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={b.description}
            >
              {b.description || "(no description)"}
            </div>
            <div style={{ flexShrink: 0 }}>
              <StatusPill blockedTill={b.blockedTill} />
            </div>
            <div
              className="tile-row-detail"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                color: "var(--portal-muted)",
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={b.number}
            >
              {b.number || "—"}
            </div>
            <div
              className="tile-row-detail"
              style={{
                flex: "1 1 0",
                minWidth: 0,
                color: "var(--portal-muted)",
                fontSize: "0.9rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={b.ani}
            >
              {b.ani || "—"}
            </div>
            <div
              className="tile-row-detail"
              style={{
                flexShrink: 0,
                color: "var(--portal-muted)",
                fontSize: "0.85rem",
              }}
            >
              {formatDate(b.blockedTill)}
            </div>
            <Pencil
              size={16}
              style={{ color: "var(--portal-primary)", flexShrink: 0 }}
            />
          </button>
        );
      })}
    </div>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
